export type AiCodeFieldSelection = { field: string; value: unknown };
export type AiCodeFieldSelections = {
  ai: Array<AiCodeFieldSelection>;
  application: Array<AiCodeFieldSelection>;
  defaults: Array<AiCodeFieldSelection>;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function fields(value: unknown, path = ''): Array<AiCodeFieldSelection> {
  const entries = Array.isArray(value)
    ? value.map((item, index) => [String(index), item] as const)
    : isObject(value)
      ? Object.entries(value)
      : [];
  if (entries.length === 0) return path ? [{ field: path, value }] : [];
  return entries.flatMap(([key, item]) =>
    fields(item, Array.isArray(value) ? `${path}[${key}]` : path ? `${path}.${key}` : key),
  );
}

/** Attribute actual payload values to AI output or application normalization, without guessing server defaults. */
export function getAiCodeFieldSelections(
  generated: unknown,
  normalized: unknown,
  schema: unknown,
  operation: 'create' | 'update',
): AiCodeFieldSelections {
  const proposed = new Map(fields(generated).map(({ field, value }) => [field, value]));
  const selections: AiCodeFieldSelections = { ai: [], application: [], defaults: [] };
  for (const entry of fields(normalized)) {
    const unchanged =
      proposed.has(entry.field) && JSON.stringify(proposed.get(entry.field)) === JSON.stringify(entry.value);
    selections[unchanged ? 'ai' : 'application'].push(entry);
  }

  function collectDefaults(value: unknown, definition: unknown, path = '') {
    if (!isObject(definition)) return;
    if (Array.isArray(value)) {
      value.forEach((item, index) => collectDefaults(item, definition.items, `${path}[${index}]`));
    } else if (isObject(value) && isObject(definition.properties)) {
      for (const [key, child] of Object.entries(definition.properties)) {
        const field = path ? `${path}.${key}` : key;
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          collectDefaults(value[key], child, field);
        } else if (isObject(child) && Object.prototype.hasOwnProperty.call(child, 'default')) {
          selections.defaults.push({ field, value: child.default });
        }
      }
    }
  }
  if (operation === 'create') collectDefaults(normalized, schema);
  return selections;
}
